"use client";

import { useEffect, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import HeaderBar from "@/components/HeaderBar";
import FooterActionBar from "@/components/FooterActionBar";
import TagItBeaconClient from "@/components/TagItBeaconClient";
import NativeBridgeBootstrap from "@/components/NativeBridgeBootstrap";
import TagItSDKLoader from "@/components/TagItSDKLoader";
import LegalAcceptanceGate from "@/components/LegalAcceptanceGate";
import PortalUnavailable from "@/components/portal/PortalUnavailable";
import PortalMembershipBootstrap from "@/components/portal/PortalMembershipBootstrap";
import { usePortal } from "@/context/PortalContext";

function canVisitWithoutAuth(pathname = "") {
  if (!pathname) return false;
  if (pathname === "/") return true;
  return (
    pathname.startsWith("/login") ||
    pathname.startsWith("/info") ||
    pathname.startsWith("/h/") ||
    pathname.startsWith("/share/")
  );
}

function isPortalBlockedRoute(pathname = "") {
  return (
    pathname === "/new-hub" ||
    pathname.startsWith("/profile/agents") ||
    pathname.startsWith("/dev")
  );
}

export default function AppChrome({ children }) {
  const portal = usePortal();
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const isAuthlessRoute = canVisitWithoutAuth(pathname);
  const isMinimalPublicRoute = pathname?.startsWith("/login") || pathname?.startsWith("/info");
  const isLegalAcceptanceExemptRoute = isMinimalPublicRoute || pathname?.startsWith("/profile/setup");

  const redirectTarget = useMemo(() => {
    const query = searchParams?.toString();
    const path = pathname || "/";
    return query ? `${path}?${query}` : path;
  }, [pathname, searchParams]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      if (!isAuthlessRoute) {
        router.replace(
          `/login?redirect=${encodeURIComponent(redirectTarget || "/")}`
        );
      }
    }
  }, [user, loading, isAuthlessRoute, redirectTarget, router]);

  if (portal?.unavailable) {
    return <PortalUnavailable title="This portal is temporarily unavailable." />;
  }
  if (portal && isPortalBlockedRoute(pathname)) {
    return <PortalUnavailable title="This page is not available in this portal." />;
  }

  if (!user) {
    if (isMinimalPublicRoute) {
      return (
        <>
          <TagItSDKLoader />
          <TagItBeaconClient />
          <NativeBridgeBootstrap />
          {children}
        </>
      );
    }
    if (isAuthlessRoute) {
      return (
        <>
          <TagItSDKLoader />
          <TagItBeaconClient />
          <NativeBridgeBootstrap />
          <HeaderBar />
          <div className="app-page mx-auto max-w-3xl px-4 pb-[calc(6rem+var(--safe-area-bottom))] pt-0">
            {children}
          </div>
          <FooterActionBar />
        </>
      );
    }
    return (
      <>
        <TagItSDKLoader />
        <TagItBeaconClient />
        <NativeBridgeBootstrap />
        <div className="min-h-[100svh] grid place-items-center bg-[var(--bg)]">
          <div className="text-sm text-[var(--muted)]">Redirecting…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <PortalMembershipBootstrap />
      <TagItSDKLoader />
      <TagItBeaconClient />
      <NativeBridgeBootstrap />
      <HeaderBar />
      <div className="app-page mx-auto max-w-3xl px-4 pb-[calc(6rem+var(--safe-area-bottom))] pt-0">
        <LegalAcceptanceGate disabled={isLegalAcceptanceExemptRoute}>
          {children}
        </LegalAcceptanceGate>
      </div>
      <FooterActionBar />
    </>
  );
}
